import React, { useCallback, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { INSTAGRAM_POST_LOADING } from "../../../../constants/loading.constant";
import { SCHEDULE_POST_MODAL } from "../../../../constants/modal.constant";
import { useFacebookCore } from "../../../../cores/facebook";
import { useGlobalCore } from "../../../../cores/globals";
import { useInstagramCore } from "../../../../cores/instagram";
import { useLinkedInCore } from "../../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../../cores/linkedInBusiness";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import {
  SocialProviderId,
  normalizeSocialProviderIdForPreview,
} from "../../../../cores/socialPost/config";
import { SOCIAL_ACTION_TYPES } from "../../../../cores/socialPost/consts/socialActionType.constant";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { PostType } from "../../../../cores/socialPost/enums/postType";
import { loadPostImagesFromMediaUrls } from "../../../../cores/socialPost/helpers/loadPostMediaFromUrls";
import { useTwitterCore } from "../../../../cores/twitter";
import { useTiktokCore } from "../../../../cores/tiktok";
import { PostActionButtons } from "./PostActionButtons";
import "./ModernPostButtons.scss";

const SOCIAL_POST_BACKGROUND_TOAST_ID = "social-post-background-publish";
const SOCIAL_POST_REEL_PROCESSING_TOAST_ID = "social-post-reel-processing";
const SOCIAL_POST_REEL_SCHEDULED_TOAST_ID = "social-post-reel-scheduled";
const REEL_SCHEDULE_SUCCESS_MESSAGE =
  "El reel será publicado al horario seleccionado";
/** Programar Reel: tiempo “Programando”; el toast dura 1 s más y al cerrarse se limpia el posteador. */
const REEL_SCHEDULE_LOADING_MS = 3000;
const REEL_SCHEDULE_TOAST_MS = 4000;

interface Props {
  from: string;
  repost: boolean;
  onReset: () => void;
  scheduleAt?: string;
  /** Calendario: validar fecha/hora sin parsear el string (mismo instante que el composer). */
  scheduleAtTimestamp?: number;
  scheduleDirty?: boolean;
  onScheduledSuccess?: () => void;
  onSavedSuccess?: () => void;
  onDeletedSuccess?: () => void;
  /** Tras programar o duplicar con éxito, recarga la grilla del calendario. */
  onPostsRefresh?: () => void;
  /** Calendario: al duplicar, pasar a modo programación (día/hora) con el mismo contenido. */
  onBeginDuplicateSchedule?: () => void;
}

export const ModernPostButtons = ({
  from,
  repost,
  onReset,
  scheduleAt,
  scheduleAtTimestamp,
  scheduleDirty = false,
  onScheduledSuccess,
  onSavedSuccess,
  onDeletedSuccess,
  onPostsRefresh,
  onBeginDuplicateSchedule,
}: Props) => {
  const lastActionRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const intl = useIntl();
  const {
    socialPost: socialPostTwitter,
    socialPostVideo: socialPostVideoTwitter,
  } = useTwitterCore();
  const {
    socialPost: socialPostFacebook,
    socialPostVideo: socialPostVideoFacebook,
  } = useFacebookCore();
  const {
    socialPost: socialPostTiktok,
    socialPostVideo: socialPostVideoTiktok,
  } = useTiktokCore();
  const { socialPost: socialPostLinkedIn } = useLinkedInCore();
  const {
    socialPost: socialPostLinkedInBusiness,
    socialPostVideo: socialPostVideoLinkedInBusiness,
  } = useLinkedInBusinessCore();
  const {
    socialPost: socialPostInstagram,
    socialPostVideo: socialPostVideoInstagram,
    cleanErrors,
  } = useInstagramCore();
  const { company, companyPlan } = useMeCore();
  const { turnOnModal, turnOffLoading } = useGlobalCore();
  const {
    socialProviders,
    postErrors,
    images,
    video,
    postType,
    text = "",
    mentions,
    setSocialAction,
    selectedPost,
    duplicateScheduleSourcePostId,
    setDuplicateScheduleSourcePostId,
  } = useSocialPostCore();

  const canSchedule = useMemo(
    () => companyPlan?.rules.allowScheduledPost,
    [companyPlan]
  );

  const hasInstagramSelected = useMemo(() => {
    return (
      socialProviders.find(
        (sp: any) =>
          sp?.id === SocialProviderId.Instagram ||
          sp?.name === SocialProviderId.Instagram
      ) != null
    );
  }, [socialProviders]);

  /** Reel de Instagram con video (publicar o programar). */
  const instagramReelPayload = useMemo(() => {
    if (!hasInstagramSelected) return false;
    const isReelType =
      postType === PostType.REEL ||
      selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO;
    if (!isReelType) return false;
    return Boolean(
      video ||
        (selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO &&
          (selectedPost?.media?.length ?? 0) > 0)
    );
  }, [
    hasInstagramSelected,
    postType,
    video,
    selectedPost?.post?.type,
    selectedPost?.media?.length,
  ]);

  const instagramScheduleOk = useMemo(() => {
    if (!hasInstagramSelected) return true;
    const instagramPostType =
      selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO
        ? PostType.REEL
        : postType;
    const requiresVideo = instagramPostType === PostType.REEL;
    const hasVideoSelected = Boolean(video);
    const hasVideoInSelectedPost =
      selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO &&
      (selectedPost?.media?.length ?? 0) > 0;
    const hasImageSelected = images.length > 0;
    const hasImageInSelectedPost =
      selectedPost?.post?.type === SOCIAL_POST_TYPES.IMAGE &&
      (selectedPost?.media?.length ?? 0) > 0;
    return requiresVideo
      ? hasVideoSelected || hasVideoInSelectedPost
      : hasImageSelected || hasImageInSelectedPost;
  }, [
    hasInstagramSelected,
    postType,
    images.length,
    video,
    selectedPost?.post?.type,
    selectedPost?.media?.length,
  ]);

  const disabled = useMemo(() => {
    const socialProviderIds = socialProviders.map((sp) => sp.id);
    const hasError =
      postErrors.find((error) =>
        socialProviderIds.includes(error.socialProviderId)
      ) != null;
    const hasMediaInDraft =
      images.length > 0 ||
      !!video ||
      (selectedPost?.media && selectedPost.media.length > 0);
    const hasText = Boolean((text || "").trim() || selectedPost?.post?.text?.trim());
    const emptyContent = !hasText && !hasMediaInDraft;
    const noNetworks = socialProviders.length === 0;
    return hasError || emptyContent || noNetworks;
  }, [
    postErrors,
    images.length,
    text,
    video,
    selectedPost?.media,
    selectedPost?.post?.text,
    socialProviders,
  ]);

  const onSuccess = useCallback(() => {
    setDuplicateScheduleSourcePostId(null);
    onReset();
    cleanErrors();
    if (
      from === "calendar" &&
      lastActionRef.current === SOCIAL_ACTION_TYPES.SCHEDULE
    ) {
      onPostsRefresh?.();
      onScheduledSuccess?.();
    }
  }, [
    setDuplicateScheduleSourcePostId,
    onReset,
    cleanErrors,
    from,
    onPostsRefresh,
    onScheduledSuccess,
  ]);

  const onOpeningSchedule = useCallback(() => {
    if (!disabled && canSchedule && instagramScheduleOk) {
      turnOnModal(SCHEDULE_POST_MODAL, true);
    }
  }, [turnOnModal, disabled, canSchedule, instagramScheduleOk]);

  const send = useCallback(
    async (
      parseDate: string,
      actionType: string,
      duplicateFromPostId?: string
    ) => {
      if (isSubmitting) return;
      lastActionRef.current = actionType;
      setSocialAction(actionType);

      let repostId = "";
      if (duplicateFromPostId) {
        repostId = duplicateFromPostId;
      } else if (
        duplicateScheduleSourcePostId &&
        selectedPost?.post?.id &&
        duplicateScheduleSourcePostId === selectedPost.post.id
      ) {
        repostId = duplicateScheduleSourcePostId;
      } else if (repost && selectedPost?.post?.id) {
        repostId = selectedPost.post.id;
      }

      if (socialProviders.length === 0) {
        toast.error(
          intl.formatMessage({
            id: "app.aiGeneration.postToSocialSelectNetworkHint",
            defaultMessage: "Selecciona al menos una red social para publicar.",
          })
        );
        return;
      }

      /** Vista previa usa `selectedPost.media` si no hay blobs en Redux; la API solo recibía `images` vacío. */
      const effectiveText =
        (text ?? "").trim() ||
        (selectedPost?.post?.text ?? "").trim();
      let imagesPayload = images;
      const needsHydrateFromUrls =
        !video &&
        images.length === 0 &&
        (selectedPost?.media?.length ?? 0) > 0 &&
        selectedPost?.post?.type !== SOCIAL_POST_TYPES.VIDEO;

      const prepToastId = "social-schedule-prepare-media";
      if (needsHydrateFromUrls) {
        toast.info(
          intl.formatMessage({
            id: "app.socialCalendar.preparingMedia",
            defaultMessage: "Preparando imágenes…",
          }),
          { toastId: prepToastId, autoClose: false }
        );
        try {
          const media = selectedPost?.media;
          if (!media?.length) {
            toast.dismiss(prepToastId);
            return;
          }
          imagesPayload = await loadPostImagesFromMediaUrls(media);
        } catch (e) {
          console.error(e);
          toast.dismiss(prepToastId);
          toast.error(
            intl.formatMessage({
              id: "app.socialMediaPost.errorMsg12",
              defaultMessage:
                "Se necesita una Imagen o Video para publicar en esta red social",
            })
          );
          return;
        }
        toast.dismiss(prepToastId);
      }

      /* Sin repostId la API necesita binarios; con duplicar/agenda desde origen el backend puede resolver la media. */
      if (
        hasInstagramSelected &&
        !video &&
        imagesPayload.length === 0 &&
        !repostId
      ) {
        toast.error(
          intl.formatMessage({
            id: "app.socialMediaPost.errorMsg12",
            defaultMessage:
              "Se necesita una Imagen o Video para publicar en esta red social",
          })
        );
        return;
      }

      const requests: Promise<void>[] = [];
      const isPublishingReel =
        actionType === SOCIAL_ACTION_TYPES.POST && instagramReelPayload;
      const isSchedulingReel =
        actionType === SOCIAL_ACTION_TYPES.SCHEDULE && instagramReelPayload;
      /** Programación: reset del posteador una sola vez al terminar todos los requests bien. */
      const perProviderSuccessCallback =
        actionType === SOCIAL_ACTION_TYPES.SCHEDULE ? undefined : onSuccess;

      socialProviders.forEach((sp) => {
        const providerKey = normalizeSocialProviderIdForPreview(
          String(sp.id ?? sp.name ?? "").trim() || undefined
        );
        switch (providerKey) {
        case SocialProviderId.Twitter:
          requests.push(
            video
            ? socialPostVideoTwitter(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
            : socialPostTwitter(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
          );
          break;
        case SocialProviderId.TikTok:
          requests.push(
            video
            ? socialPostVideoTiktok(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
            : socialPostTiktok(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
          );
          break;
        case SocialProviderId.LinkedInBusiness:
          requests.push(
            video
            ? socialPostVideoLinkedInBusiness(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
            : socialPostLinkedInBusiness(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
          );
          break;
        case SocialProviderId.LinkedIn:
          requests.push(
            socialPostLinkedIn(
              company?.id!,
              effectiveText,
              mentions,
              imagesPayload,
              parseDate,
              repostId,
              perProviderSuccessCallback
            )
          );
          break;
        case SocialProviderId.Facebook:
          requests.push(
            video
            ? socialPostVideoFacebook(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
            : socialPostFacebook(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
          );
          break;
        case SocialProviderId.Instagram:
          requests.push(
            video
            ? socialPostVideoInstagram(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
            : socialPostInstagram(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                perProviderSuccessCallback
              )
          );
          break;
        default:
          if (process.env.NODE_ENV === "development") {
            // Antes: solo sp.name; id "instagram" o name "LinkedIn Business" no coincidían y no se enviaba nada.
            console.warn("[ModernPostButtons] Proveedor sin handler de envío:", sp, "→", providerKey);
          }
          break;
      }
    });

      if (requests.length === 0) return;

      setIsSubmitting(true);

      try {
        if (isPublishingReel) {
          toast.success(
            intl.formatMessage({
              id: "app.socialMediaPost.reelProcessing",
              defaultMessage:
                "Tu Reel se está procesando y será publicado a la brevedad.",
            }),
            {
              toastId: SOCIAL_POST_REEL_PROCESSING_TOAST_ID,
              autoClose: 5000,
            }
          );
          void turnOffLoading(INSTAGRAM_POST_LOADING, 5000);
        } else if (isSchedulingReel) {
          toast.success(REEL_SCHEDULE_SUCCESS_MESSAGE, {
            toastId: SOCIAL_POST_REEL_SCHEDULED_TOAST_ID,
            autoClose: REEL_SCHEDULE_TOAST_MS,
          });
          void turnOffLoading(INSTAGRAM_POST_LOADING, REEL_SCHEDULE_LOADING_MS);
        }

        const settled = await Promise.allSettled(requests);
        const scheduleAllOk =
          actionType === SOCIAL_ACTION_TYPES.SCHEDULE &&
          settled.every((r) => r.status === "fulfilled");
        if (scheduleAllOk && from === "calendar" && isSchedulingReel) {
          onPostsRefresh?.();
        }
        if (scheduleAllOk) {
          if (isSchedulingReel) {
            /* Limpieza y cierre modal alineados al toast; el refresh ya corre arriba para ver el ítem ya. */
            window.setTimeout(() => {
              setDuplicateScheduleSourcePostId(null);
              onReset();
              cleanErrors();
              if (from === "calendar") {
                onScheduledSuccess?.();
              }
            }, REEL_SCHEDULE_TOAST_MS);
          } else {
            onSuccess();
          }
        }
        if (isPublishingReel) return;
        if (isSchedulingReel) return;

        toast.success(
          actionType === SOCIAL_ACTION_TYPES.SCHEDULE
            ? "Publicación programada correctamente."
            : "Publicación realizada correctamente.",
          { toastId: SOCIAL_POST_BACKGROUND_TOAST_ID }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      socialProviders,
      video,
      mentions,
      company,
      text,
      repost,
      images,
      selectedPost?.post?.id,
      selectedPost?.post?.text,
      selectedPost?.post?.type,
      selectedPost?.media,
      postType,
      socialPostVideoTwitter,
      socialPostTwitter,
      socialPostVideoLinkedInBusiness,
      socialPostLinkedInBusiness,
      socialPostLinkedIn,
      socialPostVideoFacebook,
      socialPostFacebook,
      socialPostVideoInstagram,
      socialPostInstagram,
      socialPostTiktok,
      socialPostVideoTiktok,
      onSuccess,
      setSocialAction,
      turnOffLoading,
      duplicateScheduleSourcePostId,
      intl,
      instagramReelPayload,
      setDuplicateScheduleSourcePostId,
      onReset,
      cleanErrors,
      from,
      onPostsRefresh,
      onScheduledSuccess,
    ]
  );

  const postNow = useCallback(() => {
    void send("", SOCIAL_ACTION_TYPES.POST).catch((e) => {
      console.error(e);
      toast.dismiss("social-schedule-prepare-media");
    });
  }, [send]);

  const canScheduleAt = useMemo(() => {
    const atOrAfterCurrentMinute = (ts: number) =>
      moment(ts).startOf("minute").valueOf() >= moment().startOf("minute").valueOf();
    if (typeof scheduleAtTimestamp === "number" && !Number.isNaN(scheduleAtTimestamp)) {
      return atOrAfterCurrentMinute(scheduleAtTimestamp);
    }
    if (!scheduleAt) return false;
    const t = moment(scheduleAt).valueOf();
    return !Number.isNaN(t) && atOrAfterCurrentMinute(t);
  }, [scheduleAt, scheduleAtTimestamp]);

  const scheduleNow = useCallback(() => {
    if (!scheduleAt) return;
    if (!canScheduleAt) return;
    if (!instagramScheduleOk) return;
    void send(scheduleAt, SOCIAL_ACTION_TYPES.SCHEDULE).catch((e) => {
      console.error(e);
      toast.dismiss("social-schedule-prepare-media");
    });
  }, [scheduleAt, canScheduleAt, instagramScheduleOk, send]);

  return (
    <div className="modern-post-buttons">
      {/* Selected providers info */}
      <div className="selected-providers-info">
        <span className="providers-label">{intl.formatMessage({ id: "app.socialPostSelector.willPublishOn" })}</span>
        <div className="providers-tags">
          {socialProviders.map((provider) => (
            <div key={provider.id} className="provider-tag">
              <span className="provider-name">{provider.displayName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        {selectedPost && !repost && !duplicateScheduleSourcePostId ? (
          <PostActionButtons
            from={from}
            save={false}
            disabled={disabled}
            onReset={onReset}
            onSavedSuccess={onSavedSuccess}
            onDeletedSuccess={onDeletedSuccess}
            onDuplicateSchedule={onBeginDuplicateSchedule}
            onPostsRefresh={onPostsRefresh}
          />
        ) : (
          <>
            <button
              className={`schedule-button ${!canSchedule ? 'disabled' : ''}`}
              disabled={
                disabled ||
                isSubmitting ||
                !canSchedule ||
                (from === "calendar" ? !canScheduleAt : false) ||
                !instagramScheduleOk
              }
              onClick={from === "calendar" ? scheduleNow : onOpeningSchedule}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>
                {isSubmitting &&
                lastActionRef.current === SOCIAL_ACTION_TYPES.SCHEDULE &&
                from === "calendar" &&
                instagramReelPayload
                  ? "Programando"
                  : intl.formatMessage({ id: "app.socialMediaPost.program" })}
              </span>
            </button>
            
            <button
              className="publish-button"
              disabled={
                disabled ||
                isSubmitting ||
                (from === "calendar"
                  ? scheduleDirty && !duplicateScheduleSourcePostId
                  : false) ||
                !instagramScheduleOk
              }
              onClick={postNow}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>
                {isSubmitting
                  ? "Procesando..."
                  : from === "calendar"
                  ? "Publicar ahora"
                  : intl.formatMessage({ id: "app.socialMediaPost.toPost" })}
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
